// Where an edit tool works — the plan, a section view's plane or a reflected
// ceiling plan — and how a point or a move there reaches the model. Pure.

import type {
  SectionLine,
  Vec2,
} from '../shared/types';
import {
  sectionFrame,
  sectionFramePoint,
  sectionView,
} from './section';
import type { Vec3 } from './tilt';
import {
  type PointMap,
  scaling,
} from './drawingObjects';

/** The plane an edit tool works in: the plan; a section view's — along its cut
 *  and up —; or a reflected ceiling plan's, its room's plan shifted by `offset`. */
export type WorkPlane =
  | { kind: 'plan' }
  | { kind: 'section'; sec: SectionLine }
  | { kind: 'rcp'; offset: Vec2 };

export const PLAN_PLANE: WorkPlane = { kind: 'plan' };

/** Where a tool works — each tool's row in main.ts's `TOOL_PLACE`:
 *  - `canvas`: on the plan's canvas as drawn — a view's drawing is ink to it
 *    (it dimensions, annotates or draws over a view);
 *  - `plan`: on the plan alone — clicked in a section view or a ceiling plan it
 *    says so (the outline tools: Offset, Trim, Fillet, Constrain);
 *  - `plane`: in the plane of the view it is used in (Mirror);
 *  - `pick`: there too, by a base point and a second point in the base point's
 *    plane (Rotate);
 *  - `pick+3d`: so, and in the 3D view by two points on what it shows, their
 *    height too (Move, Copy);
 *  - `plane+3d`: in a view's plane and in the 3D view (Select, Transform, the
 *    arrays);
 *  - `canvas+3d`: on the plan's canvas and in the 3D view, where a click works
 *    on what it is on (Measure, the room tools, Section, Zoom);
 *  - `3d`: in the 3D view (the 3D section). */
export type ToolPlace =
  | '3d'
  | 'canvas'
  | 'canvas+3d'
  | 'pick'
  | 'pick+3d'
  | 'plan'
  | 'plane'
  | 'plane+3d';

/** What a place means for a tool working there. */
export interface PlaceRules {
  /** It works in the 3D view: started from 3D the view stays (any other tool
   *  brings the plan forward). */
  in3d: boolean;
  /** It works in the plane of the view it is used in (`toolPlane`), so a room's
   *  feature selected in a drilled-into view stays selected when it starts: it
   *  moves the feature there, or says a feature only moves. */
  inPlanes: boolean;
  /** Clicked in a section view or a ceiling plan it refuses: it works on the plan. */
  planOnly: boolean;
  /** It picks a base point, then a second point in the base point's plane — typed
   *  `@dx,dy` there, `@along,up` in a section view. */
  picks: boolean;
}

export const PLACE_RULES = {
  '3d': { in3d: true, inPlanes: false, planOnly: false, picks: false },
  canvas: { in3d: false, inPlanes: false, planOnly: false, picks: false },
  'canvas+3d': { in3d: true, inPlanes: false, planOnly: false, picks: false },
  pick: { in3d: false, inPlanes: true, planOnly: false, picks: true },
  'pick+3d': { in3d: true, inPlanes: true, planOnly: false, picks: true },
  plan: { in3d: false, inPlanes: false, planOnly: true, picks: false },
  plane: { in3d: false, inPlanes: true, planOnly: false, picks: false },
  'plane+3d': { in3d: true, inPlanes: true, planOnly: false, picks: false },
} as const satisfies Record<ToolPlace, PlaceRules>;

/** The section view a plane is, else null. */
export function sectionOf(plane: WorkPlane): SectionLine | null {
  return plane.kind === 'section' ? plane.sec : null;
}

/** A point of the canvas in the plane's own frame: a section view's (along its
 *  cut from its start, up from the ground — mm); the plan's or a ceiling plan's
 *  (x, y) as drawn. */
export function toPlaneFrame(plane: WorkPlane, p: Vec2): Vec2 {
  if (plane.kind !== 'section') return { x: p.x, y: p.y };
  const q = sectionFramePoint(plane.sec, p);
  return { x: q.t, y: q.h };
}

/** `toPlaneFrame`'s inverse: a point of the plane's frame on the canvas. */
export function fromPlaneFrame(plane: WorkPlane, q: Vec2): Vec2 {
  if (plane.kind !== 'section') return { x: q.x, y: q.y };
  const { O, T, N } = sectionView(plane.sec);
  return { x: O.x + T.x * q.x + N.x * q.y, y: O.y + T.y * q.x + N.y * q.y };
}

/** What a move of `d` in the plane's frame does to the model: in a section `d.x`
 *  along its cut and `d.y` up; on the plan and in a ceiling plan the same shift,
 *  no rise. */
export function frameMove(plane: WorkPlane, d: Vec2): { dx: number; dy: number; dz: number } {
  if (plane.kind !== 'section') return { dx: d.x, dy: d.y, dz: 0 };
  const { u } = sectionFrame(plane.sec);
  return { dx: u.x * d.x, dy: u.y * d.x, dz: d.y };
}

/** What a move from `a` to `b` (canvas points, both in the plane) does to the
 *  model: a shift on the plan and a rise (mm). In a section: along its cut and up
 *  — never toward or away from the viewer; on the plan and in a ceiling plan
 *  (the plan shifted, not turned): the same shift, no rise. */
export function planeMove(plane: WorkPlane, a: Vec2, b: Vec2): { dx: number; dy: number; dz: number } {
  const qa = toPlaneFrame(plane, a), qb = toPlaneFrame(plane, b);
  return frameMove(plane, { x: qb.x - qa.x, y: qb.y - qa.y });
}

/** A canvas point of the plane as the model's: where it is on the plan and, in a
 *  section, its height. A section's point lies on its cut line (the view says
 *  nothing about depth); a ceiling plan's is its room's plan point. */
export function planePoint(plane: WorkPlane, p: Vec2): { at: Vec2; z: number | null } {
  if (plane.kind === 'rcp') return { at: { x: p.x - plane.offset.x, y: p.y - plane.offset.y }, z: null };
  if (plane.kind === 'plan') return { at: { x: p.x, y: p.y }, z: null };
  const q = toPlaneFrame(plane, p);
  return { at: onCut(plane.sec, q.x), z: q.y };
}

/** `p` held level with `from` or plumb over it — whichever it is nearer — in the
 *  plane's own frame (in a section view: along it or up it, however it is turned
 *  on the plan). */
export function levelOrPlumb(plane: WorkPlane, from: Vec2, p: Vec2): Vec2 {
  const g = toPlaneFrame(plane, from), q = toPlaneFrame(plane, p);
  return fromPlaneFrame(plane, Math.abs(q.x - g.x) >= Math.abs(q.y - g.y) ? { x: q.x, y: g.y } : { x: g.x, y: q.y });
}

/** The plan point `along` mm down a section's cut from its start. */
export function onCut(sec: SectionLine, along: number): Vec2 {
  const { u } = sectionFrame(sec);
  return { x: sec.a.x + u.x * along, y: sec.a.y + u.y * along };
}

/** Where a plan point stands along a section's cut (mm from its start). */
export function alongCut(sec: SectionLine, p: Vec2): number {
  const { u } = sectionFrame(sec);
  return (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y;
}

/** The plan line a section's vertical line `along` mm down its cut stands on: it
 *  crosses the cut there, square to it (toward and away from the viewer). */
export function acrossCut(sec: SectionLine, along: number): [Vec2, Vec2] {
  const { n } = sectionFrame(sec), p = onCut(sec, along);
  return [p, { x: p.x + n.x, y: p.y + n.y }];
}

/** Transform ▸ Scale ×k in a section view, for one object whose middle on the
 *  plan is `mid`: its place along the cut scales about `along`, its size every
 *  way. The view says nothing about depth, so its place across the cut stays:
 *  on the plan it scales about the point `along` down the cut level with `mid`
 *  across it. `foot`: its base scales about it and its heights ×k (an object
 *  scaled on its own); none, a plan scale — a room's, which keeps its heights,
 *  and so what stands in it, as when the room is scaled on the plan. */
export function sectionScaleMap(sec: SectionLine, along: number, k: number, mid: Vec2, foot?: number): PointMap {
  const o = onCut(sec, alongCut(sec, mid)), q = onCut(sec, along);
  const m = scaling({ x: mid.x + q.x - o.x, y: mid.y + q.y - o.y }, k);
  return foot == null ? m : { ...m, foot };
}

/** The level line square to a section view that a turn in it tilts about: the
 *  cut × up, so a turn from along the cut toward up is positive (right-hand). */
export function tiltAxis(sec: SectionLine): Vec3 {
  const { u } = sectionFrame(sec);
  return { x: u.y, y: -u.x, z: 0 };
}

/** The mirror plane a slanted line drawn in a section view stands for, from `qa`
 *  to `qb` in the view's frame (along, up): it holds the line and the view's
 *  direction (toward and away from the viewer). Its unit normal; null for a line
 *  of no length. */
export function slantMirrorNormal(sec: SectionLine, qa: Vec2, qb: Vec2): Vec3 | null {
  const dt = qb.x - qa.x, dh = qb.y - qa.y, l = Math.hypot(dt, dh);
  if (l < 1e-9) return null;
  const { u, n } = sectionFrame(sec);
  const e = { x: (u.x * dt) / l, y: (u.y * dt) / l, z: dh / l };
  return { x: -e.z * n.y, y: e.z * n.x, z: e.x * n.y - e.y * n.x };
}

/** What a mirror line drawn in a section view does, from `qa` to `qb` in the
 *  view's frame: within 1° of plumb it mirrors across the vertical plane square
 *  to the cut at its middle (on the plan, across `across`); within 1° of level it
 *  mirrors heights about `h`; slanted, it tilts — the plane through `at` (where
 *  the line starts, in the model) with the unit `normal`. */
export type ViewMirror =
  | { kind: 'plumb'; across: [Vec2, Vec2] }
  | { kind: 'level'; h: number }
  | { kind: 'slant'; at: Vec3; normal: Vec3 };

export function viewMirror(sec: SectionLine, qa: Vec2, qb: Vec2): ViewMirror {
  const dt = Math.abs(qb.x - qa.x), dh = Math.abs(qb.y - qa.y), tol = Math.tan(Math.PI / 180);
  if (dt <= tol * dh) return { kind: 'plumb', across: acrossCut(sec, (qa.x + qb.x) / 2) };
  if (dh <= tol * dt) return { kind: 'level', h: (qa.y + qb.y) / 2 };
  return { kind: 'slant', at: { ...onCut(sec, qa.x), z: qa.y }, normal: slantMirrorNormal(sec, qa, qb)! };
}
